<script lang="ts" setup>
import { computed, ref, onUnmounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { onBeforeRouteLeave, onBeforeRouteUpdate, useRoute, useRouter } from 'vue-router';
import type { NavigationGuardNext, RouteLocationNormalized } from 'vue-router';

import fulfillWithTimeLimit from '@exchange/helpers/fulfill-with-time-limit';
import { accountService } from '@exchange/libs/account/service/src';
import { TradingChartContainer } from '@exchange/libs/charting/ui/src';
import useMQBreakpoints from '@exchange/libs/composables/shared/src/lib/useMQBreakpoints';
import { marketService } from '@exchange/libs/market/service/src';
import MarketHeader from '@exchange/libs/market/ui/src/lib/components/MarketHeader.vue';
import { MyOrdersContainer } from '@exchange/libs/order/my-orders/ui/src';
import { orderformService } from '@exchange/libs/order/order-form/service/src';
import { OrderContainer } from '@exchange/libs/order/order-form/ui/src';
import { DepthChartContainer } from '@exchange/libs/orderbook/depth-chart/ui/src';
import OrderbookContainer from '@exchange/libs/orderbook/orderbook/ui/src/lib/OrderbookContainer.vue';
import { PriceTicksHistoryContainer } from '@exchange/libs/price-ticks/ui/src';
import { SubaccountType } from '@exchange/libs/trading-accounts/service/src';
import AccountOverview from '@exchange/libs/trading-accounts/ui/src/lib/AccountOverview.vue';
import AccountSelectDropdown from '@exchange/libs/trading-accounts/ui/src/lib/AccountSelectDropdown.vue';
import { useEmailFlow } from '@exchange/libs/transactions/email-flow/src';
import { currencyService } from '@exchange/libs/utils/currency/src';
import { logger } from '@exchange/libs/utils/simple-logger/src';

import SmartLayoutButton from './components/SmartLayoutButton.vue';
import SpotExchangeMobileView from './SpotExchangeMobileView.vue';

const props = defineProps({
  marketId: { type: String, required: true },
});
const emit = defineEmits<{
  (e: 'market-clicked', marketId: string): void;
  (e: 'mobile:go-back'): void;
}>();

const route = useRoute();
const router = useRouter();
const { t } = useI18n({ useScope: 'global' });
const { isXS, isSM, isMD } = useMQBreakpoints();
const { orchestrateEmailFlow } = useEmailFlow();

let marketChangePromise = Promise.resolve();

const { accountId, subaccountType } = accountService;
const { currencies, defaultFiatCurrency } = currencyService;
const { markets, getMarket } = marketService;

const marketIdInternal = ref(props.marketId);
const marketIdChecked = ref(false);

const market = computed(() => getMarket(marketIdInternal.value));
const marketsList = computed(() => Object.values(markets.value));

enum TabsComponent {
  orderbook = 'orderbook',
  trade = 'trade',
  depth = 'depth',
  history = 'history',
}
const getTabs = (medium: boolean) => {
  const getTabTitle = (tt: string) => () => t(`modules.componentSwitcher.${tt}`);

  const depth = {
    getTitle: getTabTitle(TabsComponent.depth),
    id: TabsComponent.depth,
    active: false,
  };
  const orderbook = {
    getTitle: getTabTitle(TabsComponent.orderbook),
    id: TabsComponent.orderbook,
    active: true,
  };
  const history = {
    getTitle: getTabTitle(TabsComponent.history),
    id: TabsComponent.history,
    active: false,
  };
  const trade = {
    getTitle: getTabTitle(TabsComponent.trade),
    id: TabsComponent.trade,
    active: false,
  };

  if (medium) {
    return [depth, orderbook, history];
  }

  return [trade, depth, orderbook, history];
};
const tabsSM = ref(getTabs(false));
const tabsMD = ref(getTabs(true));
const activeTabKey = computed(() => {
  const activeTabs = isSM.value ? tabsSM.value : tabsMD.value;
  return activeTabs.find((e) => e.active)?.id || TabsComponent.orderbook;
});

const setMarketBasedOnRoute = async (to: RouteLocationNormalized) => {
  const { marketId } = to.params;

  if (!marketId || typeof marketId !== 'string') {
    return;
  }

  try {
    const mId = await marketService.getValidMarketId({ marketId });
    marketIdInternal.value = mId;
    marketIdChecked.value = true;
  } catch (e) {
    logger.error('Something went wrong on market change:', e);
    router.push({
      name: 'exchange',
    });
  }
};

const routerGuard = (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext, condition: boolean) => {
  if (condition && to.params.marketId !== from.params.marketId) {
    marketChangePromise = marketChangePromise
      .then(() => {
        orderformService.hardReset();

        return setMarketBasedOnRoute(to);
      })
      .finally(next);
  } else {
    next();
  }
};

onBeforeRouteLeave((to, from, next) => {
  routerGuard(to, from, next, isXS.value);
});

onBeforeRouteUpdate((to, from, next) => {
  routerGuard(to, from, next, true);
  orchestrateEmailFlow(to);
});

const verifyProvidedMarketId = async () => {
  const mId = await fulfillWithTimeLimit<string>(marketService.getValidMarketId({ marketId: props.marketId }), 7_000, props.marketId);

  marketIdInternal.value = mId;
  marketIdChecked.value = true;
};

watch(
  () => props.marketId,
  () => {
    verifyProvidedMarketId();
  },
  { immediate: true },
);

orchestrateEmailFlow(route);

const myOrdersHeight = ref(220); // Initial height
const isDragging = ref(false);
const startY = ref(0);
const resizeHandleRef = ref<HTMLElement | null>(null);
const hasAccountOverview = computed(() => subaccountType.value === SubaccountType.FUTURES);

const resize = (event: PointerEvent) => {
  if (!isDragging.value) return;
  const delta = startY.value - event.clientY;
  myOrdersHeight.value = Math.max(100, Math.min(600, myOrdersHeight.value + delta));
  startY.value = event.clientY;
};

const stopResize = (event: PointerEvent) => {
  if (resizeHandleRef.value) {
    isDragging.value = false;
    resizeHandleRef.value.releasePointerCapture(event.pointerId);
    document.removeEventListener('pointermove', resize);
    document.removeEventListener('pointerup', stopResize);
  }
};

const startResize = (event: PointerEvent) => {
  if (resizeHandleRef.value) {
    isDragging.value = true;
    startY.value = event.clientY;
    resizeHandleRef.value.setPointerCapture(event.pointerId);
    document.addEventListener('pointermove', resize);
    document.addEventListener('pointerup', stopResize);
  }
};

onUnmounted(() => {
  document.removeEventListener('pointermove', resize);
  document.removeEventListener('pointerup', stopResize);
});
</script>

<template>
  <ot-loading-indicator
    v-if="!marketIdChecked"
    :until="true"
  />
  <!-- XS -->
  <spot-exchange-mobile-view
    v-else-if="isXS"
    class="spot-exchange-container-small-layout"
    :market-id="marketIdInternal"
    @go-back="emit('mobile:go-back')"
  />
  <!-- SM & MD -->
  <div
    v-else-if="isSM || isMD"
    class="spot-exchange-container-medium-layout"
  >
    <div
      class="spot-exchange-container-medium-layout__view"
      :class="{ 'spot-exchange-container-medium-layout__view--has-account-overview': hasAccountOverview }"
      :style="{ gridTemplateRows: `56px auto ${myOrdersHeight}px` }"
    >
      <div class="spot-exchange-container-medium-layout__market-header">
        <market-header
          :currencies="currencies"
          :default-fiat-currency="defaultFiatCurrency"
          :market="market"
          :markets-list="marketsList"
          :show-market-list="true"
          @market-clicked="emit('market-clicked', $event)"
        />
      </div>
      <div
        v-if="isMD && hasAccountOverview"
        class="spot-exchange-container-medium-layout__account-overview"
      >
        <account-overview />
      </div>
      <div class="spot-exchange-container-medium-layout__account-select">
        <account-select-dropdown />
      </div>
      <div class="spot-exchange-container-medium-layout__order">
        <order-container
          :key="accountId"
          :market-id="marketIdInternal"
        />
      </div>
      <div
        v-if="isMD"
        class="spot-exchange-container-medium-layout__trading-chart"
      >
        <trading-chart-container
          :key="accountId"
          :market-id="marketIdInternal"
        />
      </div>
      <div class="spot-exchange-container-medium-layout__my-orders-wrapper">
        <div
          ref="resizeHandleRef"
          class="spot-exchange-container-medium-layout__resize-handle"
          @pointerdown="startResize"
        >
          <SmartLayoutButton
            :horizontal="true"
            :resizable="true"
          />
        </div>
        <div class="spot-exchange-container-medium-layout__my-orders">
          <my-orders-container
            :key="`${accountId}-${subaccountType}`"
            :market-id="marketIdInternal"
          />
        </div>
      </div>
      <ot-module-container class="spot-exchange-container-medium-layout__switcher">
        <template #header-left-actions>
          <div class="spot-exchange-container-medium-layout__switcher-tabs">
            <ot-tabs
              v-if="isSM"
              v-model:tabs="tabsSM"
              :dense="true"
              :scrollable="false"
            />
            <ot-tabs
              v-else
              v-model:tabs="tabsMD"
              :dense="true"
              :scrollable="true"
            />
          </div>
        </template>
        <template #main-content>
          <price-ticks-history-container
            v-if="activeTabKey === TabsComponent.history"
            :market-id="marketIdInternal"
            :show-title="false"
          />
          <depth-chart-container
            v-if="activeTabKey === TabsComponent.depth"
            :market-id="marketIdInternal"
            :show-title="false"
          />
          <orderbook-container
            v-if="activeTabKey === TabsComponent.orderbook"
            :market-id="marketIdInternal"
            :show-title="false"
          />
          <trading-chart-container
            v-if="activeTabKey === TabsComponent.trade"
            :key="accountId"
            :market-id="marketIdInternal"
            :show-title="false"
          />
        </template>
      </ot-module-container>
    </div>
  </div>
  <!-- LG & XL -->
  <div
    v-else
    class="spot-exchange-container-large-layout"
  >
    <div
      class="spot-exchange-container-large-layout__view"
      :class="{ 'spot-exchange-container-large-layout__view--has-account-overview': hasAccountOverview }"
      :style="{ gridTemplateRows: `56px auto ${myOrdersHeight}px` }"
    >
      <div class="spot-exchange-container-large-layout__market-header">
        <market-header
          :currencies="currencies"
          :default-fiat-currency="defaultFiatCurrency"
          :market="market"
          :markets-list="marketsList"
          :show-market-list="true"
          @market-clicked="emit('market-clicked', $event)"
        />
      </div>
      <div
        v-if="hasAccountOverview"
        class="spot-exchange-container-large-layout__account-overview"
      >
        <account-overview />
      </div>
      <div class="spot-exchange-container-large-layout__account-select">
        <account-select-dropdown />
      </div>
      <div class="spot-exchange-container-large-layout__order">
        <order-container
          :key="accountId"
          :market-id="marketIdInternal"
        />
      </div>
      <div class="spot-exchange-container-large-layout__trading-chart">
        <trading-chart-container
          :key="accountId"
          :market-id="marketIdInternal"
        />
      </div>
      <div class="spot-exchange-container-large-layout__my-orders-wrapper">
        <div
          ref="resizeHandleRef"
          class="spot-exchange-container-large-layout__resize-handle"
          @pointerdown="startResize"
        >
          <SmartLayoutButton
            :horizontal="true"
            :resizable="true"
          />
        </div>
        <div class="spot-exchange-container-large-layout__my-orders">
          <my-orders-container
            :key="`${accountId}-${subaccountType}`"
            :market-id="marketIdInternal"
          />
        </div>
      </div>
      <ot-module-container class="spot-exchange-container-large-layout__switcher">
        <template #header-left-actions>
          <div class="spot-exchange-container-large-layout__switcher-tabs">
            <ot-tabs
              v-model:tabs="tabsMD"
              :dense="true"
              :scrollable="true"
            />
          </div>
        </template>
        <template #main-content>
          <price-ticks-history-container
            v-if="activeTabKey === TabsComponent.history"
            :market-id="marketIdInternal"
            :show-title="false"
          />
          <depth-chart-container
            v-if="activeTabKey === TabsComponent.depth"
            :market-id="marketIdInternal"
            :show-title="false"
          />
          <orderbook-container
            v-if="activeTabKey === TabsComponent.orderbook"
            :market-id="marketIdInternal"
            :show-title="false"
          />
        </template>
      </ot-module-container>
    </div>
  </div>
</template>

<style lang="scss">
.spot-exchange-container-large-layout {
  position: relative;
  display: flex;
  height: 100%;
  flex-direction: column;

  &__view {
    --layout-gap: 3px;

    display: grid;
    overflow: hidden;
    width: 100%;
    height: 100%;
    grid-gap: var(--layout-gap);
    grid-template:
      'market-header market-header       account-select' 56px
      'trading-chart components-switcher order' auto
      'my-orders     my-orders           order' auto
      / auto 280px 300px;

    &--has-account-overview {
      grid-template:
        'market-header market-header       account-select' 56px
        'trading-chart components-switcher order' auto
        'my-orders     my-orders           account-overview' auto
        / auto 280px 300px;
    }
  }

  &__market-header {
    grid-area: market-header;
  }

  &__order {
    grid-area: order;
  }

  &__trading-chart {
    grid-area: trading-chart;
  }

  &__account-select {
    grid-area: account-select;
  }

  &__my-orders-wrapper {
    position: relative;
    grid-area: my-orders;
  }

  &__account-overview {
    grid-area: account-overview;
  }

  &__my-orders {
    height: 100%;
    overflow-y: auto;
  }

  &__resize-handle {
    position: absolute;
    top: -3px;
    right: 0;
    left: 0;
    height: 6px;
    cursor: ns-resize;
    touch-action: none; // Prevents scrolling on touch devices
    user-select: none;
  }

  &__switcher {
    overflow: auto; // Add this to enable scrolling if content overflows
    grid-area: components-switcher;

    &-tabs {
      padding: 0 8px;
    }
  }
}

.spot-exchange-container-medium-layout {
  position: relative;
  display: flex;
  height: 100%;
  flex-direction: column;

  &__view {
    --layout-gap: 3px;

    display: grid;
    overflow: hidden;
    width: 100%;
    height: 100%;
    grid-gap: var(--layout-gap);

    .md & {
      grid-template:
        'market-header market-header       account-select' 56px
        'trading-chart components-switcher order' auto
        'my-orders     my-orders           order' auto
        / auto 280px 300px;

      &--has-account-overview {
        grid-template:
          'market-header market-header       account-select' 56px
          'trading-chart components-switcher order' auto
          'my-orders     my-orders           account-overview' 220px
          / auto 280px 300px;
      }
    }

    .sm & {
      grid-template:
        'market-header       account-select' 56px
        'components-switcher order' auto
        'my-orders           my-orders' auto
        / auto 300px;
    }
  }

  &__market-header {
    grid-area: market-header;
  }

  &__order {
    grid-area: order;
  }

  &__account-select {
    grid-area: account-select;
  }

  &__orderbook {
    overflow: hidden;
    grid-area: orderbook;
  }

  &__trading-chart {
    grid-area: trading-chart;
  }

  &__depth-chart {
    grid-area: depth-chart;
  }

  &__my-orders-wrapper {
    position: relative;
    grid-area: my-orders;
  }

  &__my-orders {
    height: 100%;
    overflow-y: auto;
  }

  &__resize-handle {
    position: absolute;
    top: -3px;
    right: 0;
    left: 0;
    height: 6px;
    cursor: ns-resize;
    touch-action: none;
    user-select: none;
  }

  &__switcher {
    grid-area: components-switcher;

    &-tabs {
      padding: 0 8px;
    }
  }
}
</style>
